
// redux
import { createSlice, createListenerMiddleware } from '@reduxjs/toolkit';
import { dispatch, getDailyPricingState, } from '../store';
// @type
import { BaseData, KeyName, MapLocationSettings, Market, Material, Origin, Period, SendReportResult } from 'src/@type/physical-markets/Model';

export type DailPricingStateMode = 'general' | 'locations';

export type DailPricingState = {
    //Actions
    getMaterials: () => Promise<BaseData>
    getMarkets: (OriginID: number, MaterialID: number) => Promise<Market[]>
    //Common
    isLoading: boolean
    mode: DailPricingStateMode
    error: string | null
    materials: Material[]
    units: KeyName[]
    mapLocationSettings: MapLocationSettings | null

    material: Material | null
    origin: Origin | null
    materialSideBarTrigger: boolean
    //General
    markets: Market[]
    market: Market | null
    period: Period | null
    collapsePeriods: boolean
    isOpenPeriodForm: boolean
    isOpenDailyPricingForm: boolean
    isOpenExtendedChart: boolean
    //Map
};

const initialState: DailPricingState = {
    getMaterials: () => new Promise<BaseData>(() => { }),
    getMarkets: (OriginID: number, MaterialID: number) => new Promise<Market[]>(() => { }),
    isLoading: false,
    mode: 'general',
    error: null,
    materials: [],
    units: [],
    mapLocationSettings: null,
    material: null,
    origin: null,
    materialSideBarTrigger: false,
    markets: [],
    market: null,
    period: null,
    collapsePeriods: false,
    isOpenPeriodForm: false,
    isOpenDailyPricingForm: false,
    isOpenExtendedChart: false
}

const slice = createSlice({
    name: 'daily-pricing',
    initialState,
    reducers: {
        // INIT
        init(state, action) {
            state.getMaterials = action.payload.getMaterials;
            state.getMarkets = action.payload.getMarkets;
        },
        // RESET
        reset(state) {
            state.isLoading = false;
            state.mode = 'general';
            state.error = null;
            state.materials = [];
            state.units = [];
            state.mapLocationSettings = null;
            state.material = null;
            state.origin = null;
            state.materialSideBarTrigger = false;
            state.markets = [];
            state.market = null;
            state.period = null;
            state.collapsePeriods = false;
            state.isOpenPeriodForm = false;
            state.isOpenDailyPricingForm = false;
            state.isOpenExtendedChart = false;
        },
        // START LOADING
        startLoading(state) {
            state.isLoading = true;
        },
        // END LOADING
        endLoading(state) {
            state.isLoading = false;
        },
        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        // SET MODE
        setMode(state, action) {
            state.mode = action.payload as DailPricingStateMode;
            if (state.mode !== 'locations' && !state.origin && state.material && state.material.Origins.length > 0)
                slice.caseReducers.setOrigin(state, { payload: state.material.Origins[0], type: 'setMode' });
            else if (state.mode === 'locations') {
                if (!state.material?.Origins.some(e => e.ShowMap))
                    slice.caseReducers.setMaterial(state, { payload: state.materials.find(e => e.Origins.some(e => e.ShowMap)), type: 'setMode' });
                if (state.origin && !state.origin.ShowMap)
                    slice.caseReducers.setOrigin(state, { payload: null, type: 'setMode' });
                slice.caseReducers.setMarkets(state, { payload: [], type: 'setMode' });
            }
        },
        // SET Base
        setBaseDataSuccess(state, action) {
            const base = action.payload as BaseData;
            state.materials = base.Materials;
            state.units = base.Units;
            state.mapLocationSettings = base.MapLocationSettings;
            const mat = base.Materials && base.Materials.length > 0 ? base.Materials[0] : null;
            slice.caseReducers.setMaterial(state, { payload: mat, type: 'setBaseDataSuccess' });
            state.isLoading = state.mode !== 'locations';
        },
        // SET MATERIAL
        setMaterial(state, action) {
            const mat = action.payload as Material | null;
            state.material = mat;
            const origin = state.mode === 'locations' || !mat ? null :
                !mat.Origins.some(e => e.OriginID === state.origin?.OriginID) ? mat.Origins.some(e => e) ? mat.Origins[0] : null : state.origin;
            slice.caseReducers.setOrigin(state, { payload: origin, type: 'setOrigin' });
        },
        // SET ORIGIN
        setOrigin(state, action) {
            const origin = action.payload as Origin | null;
            state.origin = origin;
            state.materialSideBarTrigger = !state.materialSideBarTrigger;
            if (state.mode === 'locations')
                slice.caseReducers.setMarkets(state, { payload: [], type: 'setOrigin' });
        },
        // SET MARKETS
        setMarkets(state, action) {
            const markets = [...action.payload as Market[]].sort((a, b) => a.Name > b.Name ? 1 : a.Name === b.Name ? 0 : -1);
            const market = markets.length > 0 ? state.market && markets.some(e => e.MarketID === state.market?.MarketID) ? state.market : markets[0] : null;
            state.markets = markets;
            slice.caseReducers.selectMarket(state, { payload: market, type: 'setMarkets' });
            state.isLoading = false;
        },
        // SELECT MARKET
        selectMarket(state, action) {
            const market = action.payload as Market | null;
            state.market = market;
            var period = market && market.Periods.length > 0 ? market.Periods[0] : null;
            if (state.period && market && market.Periods.some(e => e.Name === state.period?.Name))
                period = market.Periods.find(e => e.Name === state.period?.Name) ?? period;
            slice.caseReducers.selectPeriod(state, { payload: period, type: 'setMarket' });
        },
        // SELECT PERIOD
        selectPeriod(state, action) {
            state.period = action.payload as Period | null;
        },
        // SET PERIOD
        setPeriod(state, action) {
            const period = action.payload as Period;
            const market = state.market ? (marketReplacePeriods(state.market, period)) : state.market;
            const markets = market ? state.markets.map(e => e.MarketID === (market as Market).MarketID ? (market as Market) : e) : state.markets;
            state.markets = markets;
            state.market = market;
            state.period = period;
        },
        // TOGGLE COLLAPSE PERIODS
        toggleCollapsePeriods(state) {
            state.collapsePeriods = !state.collapsePeriods;
        },
        // OPEN DAILY PRICING FORM
        openDailyPricingForm(state) {
            state.isOpenDailyPricingForm = true;
        },
        // CLOSE DAILY PRICING FORM
        closeDailyPricingForm(state) {
            state.isOpenDailyPricingForm = false;
        },
        // OPEN PERIOD FORM
        openPeriodForm(state) {
            state.isOpenPeriodForm = true;
        },
        // CLOSE PERIOD FORM
        closePeriodForm(state) {
            state.isOpenPeriodForm = false;
        },
        // OPEN EXTENDED CHART
        openExtendedChart(state) {
            state.isOpenExtendedChart = true;
        },
        // CLOSE EXTENDED CHART
        closeExtendedChart(state) {
            state.isOpenExtendedChart = false;
        }
    },
});

const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
    predicate: (action, currentState, previousState) => {
        if (action.type && (action.type as string).startsWith(`${slice.name}/`)) {
            const newState = (currentState as any).dailyPricing as DailPricingState;
            const oldState = (previousState as any).dailyPricing as DailPricingState;
            return newState.mode !== 'locations' && ((!!newState.material && !!newState.origin && (
                !oldState.origin ||
                !oldState.material ||
                oldState.material.MaterialID !== newState.material.MaterialID ||
                oldState.origin.OriginID !== newState.origin.OriginID
            )) || (oldState.mode !== newState.mode && newState.mode === 'general'));
        }
        return false;
    },
    effect: async (action, listenerApi) => {
        const state = (listenerApi.getState() as any).dailyPricing as DailPricingState;
        if (state.material && state.origin)
            dispatch(updateMarkets(state.material, state.origin, state.getMarkets));
    },
});

//ListenerMiddleware
export const dailyPricingListenerMiddleware = listenerMiddleware.middleware;

// Reducer
export default slice.reducer;

// Actions
// ----------------------------------------------------------------------
export function onLoadDailyPricing() {
    dispatch(getBaseData());
}
// ----------------------------------------------------------------------
export function onSelectMaterial(material: Material) {
    dispatch(slice.actions.setMaterial(material));
}
// ----------------------------------------------------------------------
export function onSelectOrigin(origin: Origin) {
    dispatch(slice.actions.setOrigin(origin));
}
// ----------------------------------------------------------------------
export function onModeChange(mode: DailPricingStateMode) {
    dispatch(slice.actions.setMode(mode));
}
// ----------------------------------------------------------------------
export function onMarketSelect(market: Market) {
    dispatch(slice.actions.selectMarket(market));
}
// ----------------------------------------------------------------------
export function onPeriodSelect(period: Period) {
    dispatch(slice.actions.selectPeriod(period));
}
// ----------------------------------------------------------------------
export function onPeriodChange(period: Period) {
    dispatch(slice.actions.setPeriod(period));
}
// ----------------------------------------------------------------------
export function toggleCollapsePeriods() {
    dispatch(slice.actions.toggleCollapsePeriods());
}
// ----------------------------------------------------------------------
export function onOpenDailyPricingForm() {
    dispatch(slice.actions.openDailyPricingForm());
}
// ----------------------------------------------------------------------
export function onCloseDailyPricingForm() {
    dispatch(slice.actions.closeDailyPricingForm());
}
// ----------------------------------------------------------------------
export function onOpenPeriodForm() {
    dispatch(slice.actions.openPeriodForm());
}
// ----------------------------------------------------------------------
export function onClosePeriodForm() {
    dispatch(slice.actions.closePeriodForm());
}
// ----------------------------------------------------------------------
export function onOpenExtendedChart() {
    dispatch(slice.actions.openExtendedChart());
}
// ----------------------------------------------------------------------
export function onCloseExtendedChart() {
    dispatch(slice.actions.closeExtendedChart());
}
// ----------------------------------------------------------------------
function updateMarkets(material: Material, origin: Origin, getMarkets: (oid: number, mid: number) => Promise<Market[]>) {
    dispatch(slice.actions.startLoading());
    return async () => {
        try {
            getMarkets(origin.OriginID, material.MaterialID).then((markets: Market[]) => {
                dispatch(slice.actions.hasError(undefined));
                dispatch(slice.actions.setMarkets(markets));
            }).catch((err: string) => {
                dispatch(slice.actions.hasError(err));
                dispatch(slice.actions.setMarkets([]));
            });
        } catch (err) {
            dispatch(slice.actions.hasError(err));
        }
    }
}
// ----------------------------------------------------------------------
function getBaseData() {
    dispatch(slice.actions.reset());
    dispatch(slice.actions.startLoading());
    return async () => {
        try {
            (getDailyPricingState() as DailPricingState).getMaterials()
                .then((data) => {
                    dispatch(slice.actions.setBaseDataSuccess(data));
                }).catch((err) => {
                    dispatch(slice.actions.hasError(err));
                    dispatch(slice.actions.setBaseDataSuccess({ Materials: [], Units: [], MapLocationSettings: null } as BaseData));
                });
        } catch (err) {
            dispatch(slice.actions.hasError(err));
        }
    }
}
// ----------------------------------------------------------------------
export function loadDailyPrincingAction(getMaterials: () => Promise<BaseData>, getMarkets: (oid: number, mid: number) => Promise<Market[]>) {
    dispatch(slice.actions.init({
        getMaterials: getMaterials,
        getMarkets: getMarkets
    }));
};
// ----------------------------------------------------------------------
export function sendMarketReport(sendMarketReport: (marketID: number) => Promise<SendReportResult>, success: VoidFunction, error: (err: any) => void) {
    dispatch(slice.actions.startLoading());

    try {
        const marketID = (getDailyPricingState() as DailPricingState).market?.MarketID;
        if (!marketID) {
            dispatch(slice.actions.hasError('No selected market'));
        } else {
            sendMarketReport(marketID).then((data) => {
                if (data === 'Ok') {
                    success();
                    dispatch(slice.actions.endLoading());
                } else {
                    error('Fail sending email');
                    dispatch(slice.actions.endLoading());
                }


            }).catch((err) => {
                error(err);
                dispatch(slice.actions.endLoading());
            });
        }
    } catch (err) {
        error(err);
        dispatch(slice.actions.endLoading());
    }
}
// ----------------------------------------------------------------------
function marketReplacePeriods(m: Market, period: Period): Market {
    var a: any = {};
    Object.entries(m).map(([key, val]) => {
        if (key !== 'Periods')
            a[key] = val;
        else {
            if (m.Periods.some(e => e.id === period.id))
                a[key] = m.Periods.map(p => p.id === period.id ? period : p)
            else if (m.MarketID === period.MarketID) {
                var periods = m.Periods.map(e => e);
                periods.push(period);
                a[key] = periods;
            }
            else
                a[key] = m.Periods;
        }
    });
    return a;
}
